import * as RadixPopover from '@radix-ui/react-popover';

import { ChevronRight } from '../../icons/lucide/ChevronRight';
import { cn } from '../../utils/cn';
import { MenuListItem } from '../MenuListItem';
import type { DropdownSubTriggerProps } from './Dropdown.types';
import { useDropdownContext, useDropdownSubContext } from './DropdownContext';

/**
 * Submenu trigger row — **web** implementation.
 *
 * A `MenuListItem` with a trailing right-chevron, anchoring the flyout.
 * Hover-in opens; hover-out schedules the grace-delayed close (cancelled if
 * the pointer lands in the flyout); press toggles for touch / keyboard. The
 * row pins its `selected` treatment while the flyout is open (shadcn's
 * `data-[state=open]` highlight), with the auto-check suppressed by the
 * explicit chevron.
 */
export function DropdownSubTrigger({
  children,
  icon,
  disabled,
  className,
  testID,
}: DropdownSubTriggerProps) {
  const { size } = useDropdownContext('DropdownSubTrigger');
  const { open, toggle, openNow, scheduleClose } =
    useDropdownSubContext('DropdownSubTrigger');

  return (
    <RadixPopover.Anchor asChild>
      {/* The span (not the row) is the anchor so MenuListItem needs no ref. */}
      <span className="flex w-full shrink-0">
        <MenuListItem
          size={size}
          disabled={disabled}
          leftIcon={icon}
          rightIcon={ChevronRight}
          selected={open}
          onPress={toggle}
          onHoverIn={disabled ? undefined : openNow}
          onHoverOut={disabled ? undefined : scheduleClose}
          className={cn('shrink-0', className)}
          webProps={{ 'aria-haspopup': 'menu', 'aria-expanded': open }}
          testID={testID}>
          {children}
        </MenuListItem>
      </span>
    </RadixPopover.Anchor>
  );
}
